import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Layout, Input, Button, Form, Card, Divider, Table } from "antd";
import Loader from "../../components/Loader/Loader";
import "./AveragePricesPage.css";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import Copyright from "../../components/Copyright/Copyright";
import { getAveragePricesService, getMetalsService, postNewAveragePriceService } from "./AveragePricesPageService";
import { removeTokenFromCookies } from "../../utils/cookies";
import NewAveragePriceForm from "../../components/NewAverageForm/NewAveragePriceForm";
import Cookies from "universal-cookie";

const { Content } = Layout;

export interface AveragePricesPageInterface {}

const AveragePricesPage = (props: AveragePricesPageInterface) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [editSupplierData, setEditSupplierData] = useState(null) as any;
  const [showNewAverageForm, setShowNewAverageForm] = useState(false);
  const [metals, setMetals] = useState([]);
  const [metalsOptions, setMetalsOptions] = useState([]);
  const [averagePricesList, setAveragePricesList] = useState([]) as any;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalAveragePrices, setTotalAveragePrices] = useState(0);
  const [metalsMap, setMetalsMap] = useState({}) as any;
  const navigate = useNavigate();
  const cookies = new Cookies();
  const currOffice = cookies.get("CURR_OFFICE");

  const averagePricesColumns = [
    {
      title: "Item Number",
      key: "index",
      align: "center" as const,
      render: (text: any, record: any, index: any) => index + 1,
    },
    {
      title: "Material Name",
      key: "name",
      align: "left" as const,
      render: (record: any) =>
        metalsMap[record.metalID] !== undefined ? metalsMap[record.metalID].displayName : record.metalID,
    },
    {
      title: "Start Date",
      key: "startDate",
      align: "center" as const,
      render: (record: any) => new Date(record.startDate).toLocaleDateString("en-GB"),
    },
    {
      title: "End Date",
      key: "endDate",
      align: "center" as const,
      render: (record: any) => new Date(record.endDate).toLocaleDateString("en-GB"),
    },
    {
      title: "Average Price (RM)",
      key: "averageWeightedPrice",
      align: "center" as const,
      render: (record: any) => Number(record.averageWeightedPrice).toFixed(2),
    },
    {
      title: "Total Net Weight (kg)",
      key: "totalNetWeight",
      align: "center" as const,
      render: (record: any) => Number(record.totalNetWeight).toFixed(1),
    },
    {
      title: "Office",
      key: "office",
      align: "center" as const,
      render: (record: any) => {
        let officeNameFormat: any = {
          pg: "Penang (PG)",
          bw: "Butterworth (BW)",
        };
        return (
          <div style={{ display: "flex" }}>
            <div className={`${record.office}-square-icon`}></div>
            <div> {officeNameFormat[record.office]}</div>
          </div>
        );
      },
    },
    {
      title: "Created Date",
      key: "createdDate",
      align: "center" as const,
      render: (record: any) => new Date(record.createdTimestamp).toLocaleDateString("en-GB"),
    },
    // {
    //   title: "Created By",
    //   key: "createdByUserID",
    //   align: "center" as const,
    //   render: (record: any) => record.createdByUserID,
    // },
  ];

  useEffect(() => {
    navToSelectOffice();
    getMetals();
  }, []);

  useEffect(() => {
    getAveragePrices(currentPage);
  }, [metals]);

  const navToSelectOffice = () => {
    if (currOffice === "" || currOffice === undefined || currOffice === null) navigate("/");
  };

  const toggleSidebar = (collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
  };

  const navToLogin = () => {
    navigate("/login");
  };

  const openNewAverageForm = () => {
    setShowNewAverageForm(true);
  };

  const closeNewAverageForm = () => {
    setShowNewAverageForm(false);
  };

  const getMetals = async () => {
    try {
      setLoading(true);
      const res = await getMetalsService();
      setMetals(res.data);
      const options: any = [];
      const currMetalsMap: any = {};
      res.data.forEach((metal: any) => {
        currMetalsMap[metal.metalID] = { ...metal };
        options.push({
          label: metal.displayName,
          value: metal.metalID,
        });
      });
      setMetalsMap(currMetalsMap);
      setMetalsOptions(options);
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      console.error("getMetals error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const getAveragePrices = async (pageNumber: Number) => {
    try {
      setLoading(true);
      const res = await getAveragePricesService(String(pageNumber));
      setTotalPages(res.data.totalPages);
      setTotalAveragePrices(res.data.totalAveragePrices);
      const updatedAveragePrices = [...res.data.purchases];
      for (let averagePrice of updatedAveragePrices) {
        averagePrice.key = averagePrice.averagePriceID;
      }
      setAveragePricesList(updatedAveragePrices);
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      console.error("getAveragePrices error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  const postAveragePrice = async (metalID: string, dateRange: String[], office: string) => {
    try {
      setLoading(true);
      const payload = {
        metalID: metalID,
        startDate: dateRange[0] === null || dateRange.length === 0 ? "" : dateRange[0],
        endDate: dateRange[1] === null || dateRange.length === 0 ? "" : dateRange[1],
        office: office,
      };
      const res = await postNewAveragePriceService(payload);
      setLoading(false);
      closeNewAverageForm();
      setTimeout(async () => {
        setLoading(false);
        await getAveragePrices(1);
      }, 2000);
    } catch (e: any) {
      setLoading(false);
      console.error("getAveragePrice error", e);
      if (e.response.status === 401) {
        removeTokenFromCookies();
        navToLogin();
      }
    }
  };

  return (
    <Layout>
      <Loader loading={loading} />
      <Sidebar collapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} />
      <Content>
        <Header toggleSidebar={toggleSidebar} />
        <Row justify="center">
          <Col span={22}>
            <Row>
              <Col span={12}>
                <Row justify="start">
                  <h2 className="average-prices-title">Weighted Average Prices</h2>
                </Row>
              </Col>
              <Col span={12}>
                <Row justify="end" style={{ marginTop: "20px" }}>
                  <Button type="primary" onClick={() => openNewAverageForm()}>
                    Get New Average
                  </Button>
                </Row>
              </Col>
            </Row>
            <Divider style={{ margin: "12px 0" }} />
            <Table
              dataSource={[...averagePricesList]}
              columns={averagePricesColumns}
              rowKey={(record: any) => record.key}
              pagination={{
                pageSize: 20,
                total: totalAveragePrices,
                showSizeChanger: false,
                onChange: (page) => {
                  setCurrentPage(page);
                  getAveragePrices(page);
                },
              }}
            />
          </Col>
        </Row>
        <NewAveragePriceForm
          showForm={showNewAverageForm}
          closeForm={closeNewAverageForm}
          metalsOptions={metalsOptions}
          postAveragePrice={postAveragePrice}
        />
        <Copyright />
      </Content>
    </Layout>
  );
};

export default AveragePricesPage;
